/**
 * ATSPage.js — PeoplesDock Applicant Tracking System
 *
 * FEATURE (this version): Offer Letter generation.
 *   Backend already fully supports this (letters.routes.js handles
 *   target_type: 'candidate' for offer letters) — what was missing was
 *   purely a frontend trigger. A new "Generate Offer Letter" button
 *   appears in CandidateDetailModal for any application at the 'Offer'
 *   stage, opening GenerateOfferLetterModal to collect job title
 *   (pre-filled from the application), CTC, and start date, then calls
 *   POST /letters/generate with target_type: 'candidate' and streams
 *   the resulting PDF as a download — same pattern already used for
 *   employee letters in EmployeeList.js's GenerateLetterModal.
 *
 * PREVIOUS FIXES (unchanged):
 *   FIX 1 — ViewFeedbackModal (read-only feedback viewer)
 *   FIX 2 — InterviewsTab action column: "View Feedback" / "Feedback" buttons
 *
 * EARLIER FEATURES (unchanged):
 *
 *   FEATURE: JD Templates picker in JobFormModal
 *     - "Use Template" button next to Job Title label in Post Job modal
 *     - Opens JDTemplatesModal listing 10 pre-built IT role descriptions
 *     - Selecting a template pre-fills: title, description, employment_type
 *     - HR edits then posts — fetches GET /ats/job-templates
 *
 *   FEATURE: Interview Reschedule in InterviewsTab
 *     - New "Reschedule" button on Scheduled interviews (yellow, beside Feedback)
 *     - Opens RescheduleInterviewModal with current date/time + link pre-filled
 *     - Submits PATCH /ats/interviews/:id/reschedule
 *     - Backend re-sends notification emails to candidate + interviewer
 */
import React, { useState, useEffect } from 'react';
import {
    Card, Row, Col, Button, Badge, Table, Modal,
    Form, Spinner, Dropdown, Alert, InputGroup,
} from 'react-bootstrap';
import {
    Briefcase, Users, Plus, Download, ChevronRight, Search,
    Calendar, ArrowRight, BarChart2, Edit2, Trash2,
    Mail, Phone, FileText, RefreshCw, Eye, XCircle,
    CheckCircle, Clock, Link, Video, Star, MessageSquare,
    FileSignature,
} from 'lucide-react';
import { toast } from 'react-toastify';
import axiosInstance from '../../api/axiosConfig';
import { useAuth } from '../../context/AuthContext';
// ── Pipeline stages ───────────────────────────────────────────────────────────
const PIPELINE_STAGES = [
    'Applied', 'Screening', 'Shortlisted', 'Interview',
    'Technical', 'HR Round', 'Offer', 'Hired', 'Rejected',
];
const ACTIVE_STAGES = [
    'Applied', 'Screening', 'Shortlisted', 'Interview',
    'Technical', 'HR Round', 'Offer', 'Hired',
];
const STAGE_COLOURS = {
    Applied: 'secondary', Screening: 'info', Shortlisted: 'primary',
    Interview: 'warning', Technical: 'warning', 'HR Round': 'warning',
    Offer: 'success', Hired: 'success', Rejected: 'danger',
};
const STAGE_HEX = {
    Applied: '#64748B', Screening: '#3B82F6', Shortlisted: '#4F46E5',
    Interview: '#D97706', Technical: '#EA580C', 'HR Round': '#9333EA',
    Offer: '#16A34A', Hired: '#059669', Rejected: '#DC2626',
};
const STAGE_BG = {
    Applied:     { col: '#F1F5F9', hdr: '#64748B', border: '#CBD5E1' },
    Screening:   { col: '#EFF6FF', hdr: '#3B82F6', border: '#BFDBFE' },
    Shortlisted: { col: '#EEF2FF', hdr: '#4F46E5', border: '#C7D2FE' },
    Interview:   { col: '#FFFBEB', hdr: '#D97706', border: '#FDE68A' },
    Technical:   { col: '#FFF7ED', hdr: '#EA580C', border: '#FED7AA' },
    'HR Round':  { col: '#FDF4FF', hdr: '#9333EA', border: '#E9D5FF' },
    Offer:       { col: '#F0FDF4', hdr: '#16A34A', border: '#BBF7D0' },
    Hired:       { col: '#ECFDF5', hdr: '#059669', border: '#A7F3D0' },
    Rejected:    { col: '#FFF1F2', hdr: '#DC2626', border: '#FECDD3' },
};
// ── Helpers ───────────────────────────────────────────────────────────────────
const fmtDate = (d) => d
    ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
    : '—';
const fmtINR = (n) => n ? `₹${parseFloat(n).toLocaleString('en-IN')}` : '—';
const stageBadge = (stage) => (
    <Badge bg={STAGE_COLOURS[stage] || 'secondary'} style={{ fontSize: 11, padding: '4px 8px' }}>
        {stage}
    </Badge>
);
const safeArray = (res) => {
    const d     = res?.data;
    const inner = d?.data;
    if (Array.isArray(d))                return d;
    if (Array.isArray(inner))            return inner;
    if (Array.isArray(inner?.employees)) return inner.employees;
    if (Array.isArray(inner?.data))      return inner.data;
    if (Array.isArray(d?.employees))     return d.employees;
    if (Array.isArray(d?.users))         return d.users;
    return [];
};
// ── Avatar initials ───────────────────────────────────────────────────────────
const AvatarInitials = ({ name = '', size = 30, bgColor = '#4F46E5' }) => {
    const initials = name.trim().split(/\s+/).map(w => w[0] || '').join('').slice(0, 2).toUpperCase() || '?';
    return (
        <div style={{
            width: size, height: size, borderRadius: '50%', flexShrink: 0,
            background: `linear-gradient(135deg, ${bgColor}, #7C3AED)`,
            color: '#fff', fontWeight: 700, fontSize: size * 0.38,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
            {initials}
        </div>
    );
};
const InfoRow = ({ label, value, children }) => (
    <div className="mb-3">
        <div className="text-muted" style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 2 }}>
            {label}
        </div>
        <div style={{ fontSize: 14, color: '#111827' }}>
            {children || value || <span className="text-muted">—</span>}
        </div>
    </div>
);
const ConfirmModal = ({ show, title, message, onConfirm, onCancel, loading, confirmLabel = 'Delete', confirmVariant = 'danger' }) => (
    <Modal show={show} onHide={onCancel} centered size="sm">
        <Modal.Header closeButton className="border-0">
            <Modal.Title className="fw-bold fs-6">{title}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center py-2">
            <p className="text-muted small mb-0">{message}</p>
        </Modal.Body>
        <Modal.Footer className="border-0 justify-content-center gap-2">
            <Button variant="light" onClick={onCancel} disabled={loading}>Cancel</Button>
            <Button variant={confirmVariant} onClick={onConfirm} disabled={loading}>
                {loading ? <Spinner size="sm" /> : confirmLabel}
            </Button>
        </Modal.Footer>
    </Modal>
);
// ════════════════════════════════════════════════════════════════════════════
// FEATURE: GENERATE OFFER LETTER MODAL
// ════════════════════════════════════════════════════════════════════════════
const GenerateOfferLetterModal = ({ show, candidate, application, onClose }) => {
    const [jobTitle,   setJobTitle]   = useState('');
    const [ctc,        setCtc]        = useState('');
    const [startDate,  setStartDate]  = useState('');
    const [generating, setGenerating] = useState(false);
    useEffect(() => {
        if (show) {
            setJobTitle(application?.job_title || '');
            setCtc('');
            setStartDate('');
        }
    }, [show, application]);
    const isValid = jobTitle.trim() && ctc && startDate;
    const generate = async () => {
        if (!isValid) {
            toast.error('Please fill in job title, CTC, and start date');
            return;
        }
        setGenerating(true);
        try {
            const res = await axiosInstance.post('/letters/generate', {
                letter_type: 'offer',
                target_type: 'candidate',
                target_id:   candidate.id,
                extra_fields: {
                    job_title:  jobTitle.trim(),
                    ctc,
                    start_date: startDate,
                },
            }, { responseType: 'blob' });
            const fullName = [candidate.first_name, candidate.last_name].filter(Boolean).join('_');
            const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
            const a   = document.createElement('a');
            a.href     = url;
            a.download = `Offer_Letter_${fullName}.pdf`;
            a.click();
            window.URL.revokeObjectURL(url);
            toast.success('Offer letter generated and downloaded');
            onClose();
        } catch (err) {
            let message = 'Failed to generate offer letter';
            try {
                if (err.response?.data instanceof Blob) {
                    const text = await err.response.data.text();
                    const parsed = JSON.parse(text);
                    message = parsed.message || message;
                } else {
                    message = err.response?.data?.message || message;
                }
            } catch {}
            toast.error(message);
        } finally {
            setGenerating(false);
        }
    };
    if (!candidate) return null;
    const fullName = [candidate.first_name, candidate.last_name].filter(Boolean).join(' ') || '—';
    return (
        <Modal show={show} onHide={onClose} centered backdrop="static">
            <Modal.Header closeButton className="border-0">
                <Modal.Title className="fw-bold fs-6">
                    <FileSignature size={18} className="me-2 text-primary"/>
                    Generate Offer Letter — {fullName}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Alert variant="info" className="small py-2 mb-3">
                    This generates a formal offer letter PDF for the candidate.
                    They are not yet an employee — these details aren't stored
                    anywhere in PeoplesDock except inside the letter itself.
                </Alert>
                <Row className="g-3">
                    <Col xs={12}>
                        <Form.Label className="small fw-bold">Job Title *</Form.Label>
                        <Form.Control value={jobTitle} onChange={e => setJobTitle(e.target.value)}
                            placeholder="e.g. Senior React Developer"/>
                    </Col>
                    <Col xs={12}>
                        <Form.Label className="small fw-bold">Annual CTC (₹) *</Form.Label>
                        <InputGroup>
                            <InputGroup.Text>₹</InputGroup.Text>
                            <Form.Control type="number" min="0" placeholder="e.g. 1200000"
                                value={ctc} onChange={e => setCtc(e.target.value)}/>
                        </InputGroup>
                    </Col>
                    <Col xs={12}>
                        <Form.Label className="small fw-bold">Proposed Start Date *</Form.Label>
                        <Form.Control type="date" value={startDate}
                            onChange={e => setStartDate(e.target.value)}/>
                    </Col>
                </Row>
                <div className="d-flex gap-2 mt-4">
                    <Button variant="light" className="flex-grow-1" onClick={onClose}>Cancel</Button>
                    <Button variant="primary" className="flex-grow-1 fw-bold"
                        disabled={!isValid || generating} onClick={generate}>
                        {generating
                            ? <><Spinner size="sm" className="me-2"/>Generating…</>
                            : <><Download size={14} className="me-1"/>Generate &amp; Download</>}
                    </Button>
                </div>
            </Modal.Body>
        </Modal>
    );
};
// ════════════════════════════════════════════════════════════════════════════
// FEATURE: JD TEMPLATES PICKER MODAL
// ════════════════════════════════════════════════════════════════════════════
const JDTemplatesModal = ({ show, onClose, onSelect }) => {
    const [templates, setTemplates] = useState([]);
    const [loading,   setLoading]   = useState(false);
    const [search,    setSearch]    = useState('');
    const [selected,  setSelected]  = useState(null);
    useEffect(() => {
        if (!show) return;
        setSearch(''); setSelected(null); setLoading(true);
        axiosInstance.get('/ats/job-templates')
            .then(r => setTemplates(r.data.data || []))
            .catch(() => toast.error('Failed to load templates'))
            .finally(() => setLoading(false));
    }, [show]);
    const filtered = templates.filter(t =>
        !search || t.role.toLowerCase().includes(search.toLowerCase()) ||
        t.skills.toLowerCase().includes(search.toLowerCase())
    );
    const handleUse = () => {
        if (!selected) return;
        onSelect({ title: selected.role, description: selected.description, employment_type: selected.employment_type });
        onClose();
    };
    return (
        <Modal show={show} onHide={onClose} centered size="lg" backdrop="static">
            <Modal.Header closeButton className="border-0">
                <div>
                    <Modal.Title className="fw-bold fs-6">📋 Job Description Templates</Modal.Title>
                    <div className="text-muted small mt-1">Pick a template to pre-fill your job form. You can edit it after.</div>
                </div>
            </Modal.Header>
            <Modal.Body style={{ maxHeight: '72vh', overflowY: 'auto' }}>
                <InputGroup size="sm" className="mb-3">
                    <InputGroup.Text><Search size={13} /></InputGroup.Text>
                    <Form.Control placeholder="Search by role or skills…" value={search} onChange={e => setSearch(e.target.value)} autoFocus />
                </InputGroup>
                {loading ? (
                    <div className="text-center py-4"><Spinner animation="border" variant="primary" /></div>
                ) : filtered.length === 0 ? (
                    <Alert variant="info" className="small">No templates match your search.</Alert>
                ) : (
                    <Row className="g-2">
                        {filtered.map((t, i) => {
                            const isSelected = selected?.role === t.role;
                            return (
                                <Col xs={12} md={6} key={i}>
                                    <div onClick={() => setSelected(t)} style={{
                                        border: `2px solid ${isSelected ? '#4f46e5' : '#e5e7eb'}`,
                                        borderRadius: 12, padding: '12px 14px', cursor: 'pointer',
                                        background: isSelected ? '#eef2ff' : '#fff', transition: 'all 0.15s',
                                    }}>
                                        <div className="d-flex align-items-center gap-2 mb-1">
                                            <div className="fw-bold" style={{ fontSize: 14, color: isSelected ? '#4f46e5' : '#1e293b' }}>{t.role}</div>
                                            {isSelected && <CheckCircle size={14} color="#4f46e5" className="ms-auto" />}
                                        </div>
                                        <div style={{ fontSize: 11, color: '#64748b' }}>{t.employment_type}</div>
                                        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
                                            {t.skills.split(', ').slice(0, 4).map(s => (
                                                <span key={s} style={{ display: 'inline-block', background: '#f1f5f9', borderRadius: 4, padding: '1px 6px', marginRight: 4, marginBottom: 3 }}>{s}</span>
                                            ))}
                                        </div>
                                    </div>
                                </Col>
                            );
                        })}
                    </Row>
                )}
                {selected && (
                    <div className="mt-3 p-3 rounded" style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                        <div className="fw-bold small mb-2">Preview — {selected.role}</div>
                        <div style={{ fontSize: 12, color: '#374151', whiteSpace: 'pre-line', maxHeight: 160, overflowY: 'auto', lineHeight: 1.7 }}>
                            {selected.description}
                        </div>
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer className="border-0">
                <Button variant="light" onClick={onClose}>Cancel</Button>
                <Button variant="primary" disabled={!selected} onClick={handleUse}
                    style={{ background: 'linear-gradient(135deg,#4f46e5,#7c3aed)', border: 'none' }}>
                    Use This Template →
                </Button>
            </Modal.Footer>
        </Modal>
    );
};
// ════════════════════════════════════════════════════════════════════════════
// FEATURE: RESCHEDULE INTERVIEW MODAL
// ════════════════════════════════════════════════════════════════════════════
const RescheduleInterviewModal = ({ show, interview, onClose, onDone }) => {
    const [scheduledAt, setScheduledAt] = useState('');
    const [meetingLink, setMeetingLink] = useState('');
    const [mode,        setMode]        = useState('Video Call');
    const [saving,      setSaving]      = useState(false);
    const [errors,      setErrors]      = useState({});
    useEffect(() => {
        if (!show || !interview) return;
        setMode(interview.mode || 'Video Call');
        setMeetingLink(interview.meeting_link || '');
        setErrors({});
        if (interview.scheduled_at) {
            try {
                const d = new Date(interview.scheduled_at);
                const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
                setScheduledAt(local);
            } catch { setScheduledAt(''); }
        } else {
            setScheduledAt('');
        }
    }, [show, interview]);
    const validate = () => {
        const e = {};
        if (!scheduledAt) e.scheduledAt = 'Please select a new date and time';
        else if (new Date(scheduledAt) < new Date()) e.scheduledAt = 'Rescheduled date must be in the future';
        if (mode === 'Video Call' && meetingLink.trim()) {
            try { new URL(meetingLink.trim()); }
            catch { e.meetingLink = 'Enter a valid URL (https://…)'; }
        }
        return e;
    };
    const submit = async () => {
        const e = validate();
        if (Object.keys(e).length) { setErrors(e); return; }
        setSaving(true);
        try {
            await axiosInstance.patch(`/ats/interviews/${interview.id}/reschedule`, {
                scheduled_at: scheduledAt,
                mode,
                meeting_link: mode === 'Video Call' ? (meetingLink.trim() || null) : null,
            });
            toast.success('Interview rescheduled! Notification emails sent to candidate and interviewer.');
            onDone?.();
            onClose();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to reschedule interview');
        } finally { setSaving(false); }
    };
    if (!interview) return null;
    return (
        <Modal show={show} onHide={onClose} centered size="md" backdrop="static">
            <Modal.Header closeButton className="border-0">
                <div>
                    <Modal.Title className="fw-bold fs-6">Reschedule Interview</Modal.Title>
                    <div className="text-muted small mt-1">
                        <strong>{interview.candidate_name}</strong> — {interview.job_title}
                    </div>
                </div>
            </Modal.Header>
            <Modal.Body>
                <Alert variant="info" className="small py-2 mb-3">
                    📧 Notification emails will be sent automatically to the candidate and interviewer with the new time.
                </Alert>
                <Row className="g-3">
                    <Col xs={12} md={6}>
                        <Form.Label className="small fw-bold">New Date &amp; Time <span className="text-danger">*</span></Form.Label>
                        <Form.Control type="datetime-local" value={scheduledAt} isInvalid={!!errors.scheduledAt}
                            min={new Date().toISOString().slice(0, 16)}
                            onChange={e => { setScheduledAt(e.target.value); setErrors(p => ({ ...p, scheduledAt: '' })); }} />
                        <Form.Control.Feedback type="invalid">{errors.scheduledAt}</Form.Control.Feedback>
                    </Col>
                    <Col xs={12} md={6}>
                        <Form.Label className="small fw-bold">Mode</Form.Label>
                        <Form.Select value={mode} onChange={e => setMode(e.target.value)}>
                            <option value="Video Call">📹 Video Call</option>
                            <option value="Phone">📞 Phone</option>
                            <option value="In-Person">🏢 In-Person</option>
                        </Form.Select>
                    </Col>
                    {mode === 'Video Call' && (
                        <Col xs={12}>
                            <Form.Label className="small fw-bold">Meeting Link <span className="text-muted fw-normal ms-1">(Google Meet, Teams, Zoom)</span></Form.Label>
                            <InputGroup>
                                <InputGroup.Text style={{ background: '#eff6ff', border: '1px solid #bfdbfe' }}>
                                    <Video size={14} color="#3b82f6" />
                                </InputGroup.Text>
                                <Form.Control type="url" placeholder="https://meet.google.com/xxx-xxx-xxx"
                                    value={meetingLink} isInvalid={!!errors.meetingLink}
                                    onChange={e => { setMeetingLink(e.target.value); setErrors(p => ({ ...p, meetingLink: '' })); }} />
                                <Form.Control.Feedback type="invalid">{errors.meetingLink}</Form.Control.Feedback>
                            </InputGroup>
                        </Col>
                    )}
                    <Col xs={12}>
                        <div className="p-2 rounded small" style={{ background: '#fafafa', border: '1px solid #e5e7eb' }}>
                            <span className="text-muted">Previously scheduled: </span>
                            <strong>{fmtDate(interview.scheduled_at)}</strong>
                            {interview.mode && <> · {interview.mode}</>}
                        </div>
                    </Col>
                </Row>
            </Modal.Body>
            <Modal.Footer className="border-0 pt-0">
                <Button variant="light" onClick={onClose} disabled={saving}>Cancel</Button>
                <Button onClick={submit} disabled={saving}
                    style={{ background: 'linear-gradient(135deg,#4f46e5,#7c3aed)', border: 'none' }}>
                    {saving
                        ? <><Spinner size="sm" className="me-2" />Rescheduling…</>
                        : <><Calendar size={14} className="me-2" />Confirm Reschedule</>
                    }
                </Button>
            </Modal.Footer>
        </Modal>
    );
};
// ════════════════════════════════════════════════════════════════════════════
// SCHEDULE INTERVIEW MODAL — unchanged
// ════════════════════════════════════════════════════════════════════════════
const ScheduleInterviewModal = ({ show, application, candidateName, onClose, onDone }) => {
    const [form, setForm] = useState({ interviewer_id: '', scheduled_at: '', mode: 'Video Call', meeting_link: '' });
    const [errors, setErrors] = useState({});
    const [employees, setEmployees] = useState([]);
    const [loadingEmps, setLoadingEmps] = useState(false);
    const [saving, setSaving] = useState(false);
    useEffect(() => {
        if (!show) return;
        setForm({ interviewer_id: '', scheduled_at: '', mode: 'Video Call', meeting_link: '' });
        setErrors({});
        setLoadingEmps(true);
        axiosInstance.get('/employees')
            .then(r => { const d = r.data?.data; setEmployees(Array.isArray(d) ? d : (Array.isArray(d?.employees) ? d.employees : safeArray(r))); })
            .catch(() => toast.error('Could not load employee list'))
            .finally(() => setLoadingEmps(false));
    }, [show]);
    const set = (k, v) => { setForm(p => ({ ...p, [k]: v })); if (errors[k]) setErrors(p => { const n = { ...p }; delete n[k]; return n; }); };
    const validate = () => {
        const e = {};
        if (!form.interviewer_id) e.interviewer_id = 'Please select an interviewer';
        if (!form.scheduled_at)   e.scheduled_at   = 'Please select date and time';
        else if (new Date(form.scheduled_at) < new Date()) e.scheduled_at = 'Interview date must be in the future';
        if (form.mode === 'Video Call' && form.meeting_link.trim()) {
            try { new URL(form.meeting_link.trim()); } catch { e.meeting_link = 'Enter a valid URL'; }
        }
        return e;
    };
    const submit = async () => {
        const e = validate();
        if (Object.keys(e).length) { setErrors(e); return; }
        setSaving(true);
        try {
            await axiosInstance.post('/ats/interviews', {
                application_id: application.id, interviewer_id: form.interviewer_id,
                scheduled_at: form.scheduled_at, mode: form.mode,
                meeting_link: form.mode === 'Video Call' ? (form.meeting_link.trim() || null) : null,
            });
            toast.success('Interview scheduled! Confirmation email sent to candidate.');
            onDone?.(); onClose();
        } catch (err) { toast.error(err.response?.data?.message || 'Failed to schedule interview'); }
        finally { setSaving(false); }
    };
    const selectedEmp = employees.find(e => String(e.id) === String(form.interviewer_id));
    return (
        <Modal show={show} onHide={onClose} centered size="md" backdrop="static">
            <Modal.Header closeButton className="border-0">
                <div>
                    <Modal.Title className="fw-bold fs-6">Schedule Interview</Modal.Title>
                    {application && (
                        <div className="text-muted small mt-1">
                            <strong>{candidateName}</strong> — {application.job_title}
                            <Badge className="ms-2" bg={STAGE_COLOURS[application.stage] || 'secondary'} style={{ fontSize: 10 }}>{application.stage}</Badge>
                        </div>
                    )}
                </div>
            </Modal.Header>
            <Modal.Body>
                <Row className="g-3">
                    <Col xs={12}>
                        <Form.Label className="small fw-bold">Interviewer <span className="text-danger">*</span></Form.Label>
                        {loadingEmps ? (
                            <div className="d-flex align-items-center gap-2 text-muted small py-2"><Spinner size="sm" />Loading…</div>
                        ) : (
                            <>
                                <Form.Select value={form.interviewer_id} isInvalid={!!errors.interviewer_id} onChange={e => set('interviewer_id', e.target.value)}>
                                    <option value="">— Select interviewer —</option>
                                    {employees.map(emp => (
                                        <option key={emp.id} value={emp.id}>
                                            {[emp.first_name, emp.last_name].filter(Boolean).join(' ')}
                                            {emp.designation_name ? ` — ${emp.designation_name}` : ''}
                                            {emp.department_name  ? ` (${emp.department_name})`  : ''}
                                        </option>
                                    ))}
                                </Form.Select>
                                <Form.Control.Feedback type="invalid">{errors.interviewer_id}</Form.Control.Feedback>
                                {selectedEmp && (
                                    <div className="d-flex align-items-center gap-2 mt-2 p-2 rounded" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                                        <AvatarInitials name={`${selectedEmp.first_name || ''} ${selectedEmp.last_name || ''}`} size={28} bgColor="#059669" />
                                        <div>
                                            <div className="fw-semibold small">{selectedEmp.first_name} {selectedEmp.last_name}</div>
                                            <div className="text-muted" style={{ fontSize: 11 }}>{selectedEmp.designation_name || selectedEmp.role || 'Employee'}{selectedEmp.email ? ` · ${selectedEmp.email}` : ''}</div>
                                        </div>
                                        <CheckCircle size={16} color="#059669" className="ms-auto" />
                                    </div>
                                )}
                            </>
                        )}
                    </Col>
                    <Col xs={12} md={6}>
                        <Form.Label className="small fw-bold">Date &amp; Time <span className="text-danger">*</span></Form.Label>
                        <Form.Control type="datetime-local" value={form.scheduled_at} isInvalid={!!errors.scheduled_at}
                            min={new Date().toISOString().slice(0, 16)} onChange={e => set('scheduled_at', e.target.value)} />
                        <Form.Control.Feedback type="invalid">{errors.scheduled_at}</Form.Control.Feedback>
                    </Col>
                    <Col xs={12} md={6}>
                        <Form.Label className="small fw-bold">Interview Mode</Form.Label>
                        <Form.Select value={form.mode} onChange={e => set('mode', e.target.value)}>
                            <option value="Video Call">📹 Video Call</option>
                            <option value="Phone">📞 Phone</option>
                            <option value="In-Person">🏢 In-Person</option>
                        </Form.Select>
                    </Col>
                    {form.mode === 'Video Call' && (
                        <Col xs={12}>
                            <Form.Label className="small fw-bold">Meeting Link</Form.Label>
                            <InputGroup>
                                <InputGroup.Text style={{ background: '#eff6ff', border: '1px solid #bfdbfe' }}><Video size={14} color="#3b82f6" /></InputGroup.Text>
                                <Form.Control type="url" placeholder="https://meet.google.com/xxx-xxx-xxx"
                                    value={form.meeting_link} isInvalid={!!errors.meeting_link} onChange={e => set('meeting_link', e.target.value)} />
                                <Form.Control.Feedback type="invalid">{errors.meeting_link}</Form.Control.Feedback>
                            </InputGroup>
                            <div className="text-muted mt-1" style={{ fontSize: 11 }}>This link will be included in the confirmation email.</div>
                        </Col>
                    )}
                    {form.mode === 'In-Person' && <Col xs={12}><Alert variant="info" className="py-2 small mb-0">🏢 In-Person — candidate will be contacted with venue details separately.</Alert></Col>}
                    {form.mode === 'Phone'     && <Col xs={12}><Alert variant="info" className="py-2 small mb-0">📞 Phone — the interviewer will call the candidate at the scheduled time.</Alert></Col>}
                </Row>
            </Modal.Body>
            <Modal.Footer className="border-0 pt-0">
                <Button variant="light" onClick={onClose} disabled={saving}>Cancel</Button>
                <Button onClick={submit} disabled={saving || loadingEmps} style={{ background: 'linear-gradient(135deg,#4f46e5,#7c3aed)', border: 'none' }}>
                    {saving ? <><Spinner size="sm" className="me-2" />Scheduling…</> : <><Calendar size={14} className="me-2" />Schedule Interview</>}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};
// ════════════════════════════════════════════════════════════════════════════
// CANDIDATE DETAIL MODAL — FEATURE: Generate Offer Letter button added
// ════════════════════════════════════════════════════════════════════════════
const CandidateDetailModal = ({ candidate, onClose }) => {
    const [full, setFull] = useState(null);
    const [loadingFull, setLoadingFull] = useState(false);
    const [movingApp, setMovingApp] = useState(null);
    const [editingStage, setEditingStage] = useState({});
    const [savingStage, setSavingStage] = useState(null);
    const [rejectApp, setRejectApp] = useState(null);
    const [rejectLoading, setRejectLoading] = useState(false);
    const [scheduleApp, setScheduleApp] = useState(null);
    // FEATURE: offer letter modal state — holds the application currently
    // generating an offer letter (null when closed)
    const [offerLetterApp, setOfferLetterApp] = useState(null);
    useEffect(() => {
        if (!candidate?.id) return;
        setFull(null); setLoadingFull(true);
        axiosInstance.get(`/ats/candidates/${candidate.id}`)
            .then(r => setFull(r.data.data)).catch(() => toast.error('Could not load candidate details')).finally(() => setLoadingFull(false));
    }, [candidate?.id]);
    const refresh = () => {
        if (!candidate?.id) return;
        axiosInstance.get(`/ats/candidates/${candidate.id}`).then(r => setFull(r.data.data)).catch(() => {});
    };
    const moveStage = async (appId, newStage) => {
        setMovingApp(appId);
        try { await axiosInstance.patch(`/ats/applications/${appId}/stage`, { stage: newStage }); toast.success(`Moved to ${newStage}`); refresh(); }
        catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
        finally { setMovingApp(null); }
    };
    const saveStageEdit = async (appId) => {
        const newStage = editingStage[appId]; if (!newStage) return;
        setSavingStage(appId);
        try {
            await axiosInstance.patch(`/ats/applications/${appId}/stage`, { stage: newStage });
            toast.success(`Stage updated to ${newStage}`);
            setEditingStage(p => { const n = { ...p }; delete n[appId]; return n; }); refresh();
        } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
        finally { setSavingStage(null); }
    };
    const handleReject = async () => {
        if (!rejectApp) return; setRejectLoading(true);
        try { await axiosInstance.patch(`/ats/applications/${rejectApp.id}/stage`, { stage: 'Rejected' }); toast.success('Application rejected'); setRejectApp(null); refresh(); }
        catch (err) { toast.error(err.response?.data?.message || 'Failed to reject'); }
        finally { setRejectLoading(false); }
    };
    const cand = full || candidate;
    const fullName = [cand?.first_name, cand?.last_name].filter(Boolean).join(' ') || '—';
    return (
        <>
            <Modal show onHide={onClose} centered size="lg" backdrop="static">
                <Modal.Header closeButton className="border-0 pb-0">
                    <div className="d-flex align-items-center gap-3">
                        <AvatarInitials name={fullName} size={50} />
                        <div><Modal.Title className="fw-bold fs-5 mb-0">{fullName}</Modal.Title><div className="text-muted small">{cand?.email}</div></div>
                    </div>
                </Modal.Header>
                <Modal.Body>
                    {loadingFull ? (
                        <div className="text-center py-4"><Spinner animation="border" size="sm" /><span className="text-muted small ms-2">Loading…</span></div>
                    ) : (
                        <>
                            <div className="p-3 rounded mb-4" style={{ background: '#f8fafc', border: '1px solid #e5e7eb' }}>
                                <div className="fw-semibold small mb-3" style={{ color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5 }}>Candidate Profile</div>
                                <Row className="g-0">
                                    <Col xs={12} md={4}><InfoRow label="Email"><a href={`mailto:${cand?.email}`} style={{ color: '#4f46e5' }}><Mail size={13} className="me-1" />{cand?.email || '—'}</a></InfoRow></Col>
                                    <Col xs={12} md={4}><InfoRow label="Phone">{cand?.phone ? <a href={`tel:${cand.phone}`} style={{ color: '#374151' }}><Phone size={13} className="me-1" />{cand.phone}</a> : <span className="text-muted">—</span>}</InfoRow></Col>
                                    <Col xs={12} md={4}><InfoRow label="Resume">{cand?.resume ? <a href={`${process.env.REACT_APP_API_URL?.replace('/api/v1', '')}/${cand.resume}`} target="_blank" rel="noopener noreferrer" className="text-success fw-medium"><Download size={13} className="me-1" />Download Resume</a> : <span className="text-muted small">No resume uploaded</span>}</InfoRow></Col>
                                </Row>
                            </div>
                            <div className="fw-semibold small mb-3 d-flex align-items-center gap-2">Applications {full && <Badge bg="secondary" pill style={{ fontSize: 10 }}>{(full.applications || []).length}</Badge>}</div>
                            {!full ? <Alert variant="warning" className="small py-2">Could not load applications.</Alert>
                            : (full.applications || []).length === 0 ? <Alert variant="info" className="small py-2">No applications yet.</Alert>
                            : (
                                <div className="d-flex flex-column gap-3">
                                    {full.applications.map((app, i) => {
                                        const bg = STAGE_BG[app.stage] || STAGE_BG['Applied'];
                                        const isTerminal = app.stage === 'Hired' || app.stage === 'Rejected';
                                        const isSaving   = savingStage === app.id;
                                        const isMoving   = movingApp === app.id;
                                        const editStage  = editingStage[app.id];
                                        const idx        = PIPELINE_STAGES.indexOf(app.stage);
                                        const nextStage  = PIPELINE_STAGES[idx + 1];
                                        return (
                                            <div key={i} style={{ borderRadius: 12, border: `1px solid ${bg.border}`, borderLeft: `4px solid ${bg.hdr}`, background: '#fff', overflow: 'hidden' }}>
                                                <div className="d-flex align-items-center justify-content-between px-3 py-2" style={{ background: bg.col }}>
                                                    <div><div className="fw-semibold" style={{ fontSize: 14 }}>{app.job_title || '—'}</div><div className="text-muted" style={{ fontSize: 11 }}>Applied {fmtDate(app.applied_at)}</div></div>
                                                    {stageBadge(app.stage)}
                                                </div>
                                                <div className="px-3 py-3">
                                                    {!isTerminal && (
                                                        <div className="mb-3">
                                                            <Form.Label className="small fw-bold mb-1">Update Stage</Form.Label>
                                                            <div className="d-flex gap-2 align-items-center">
                                                                <Form.Select size="sm" value={editStage !== undefined ? editStage : app.stage}
                                                                    onChange={e => setEditingStage(p => ({ ...p, [app.id]: e.target.value }))} style={{ maxWidth: 180 }}>
                                                                    {PIPELINE_STAGES.map(s => <option key={s} value={s}>{s}</option>)}
                                                                </Form.Select>
                                                                {editStage && editStage !== app.stage && (
                                                                    <Button size="sm" variant="success" style={{ borderRadius: 8, fontSize: 12 }} disabled={isSaving} onClick={() => saveStageEdit(app.id)}>
                                                                        {isSaving ? <Spinner size="sm" /> : <><CheckCircle size={12} className="me-1" />Save</>}
                                                                    </Button>
                                                                )}
                                                                {editStage && editStage !== app.stage && (
                                                                    <Button size="sm" variant="light" style={{ borderRadius: 8, fontSize: 12 }}
                                                                        onClick={() => setEditingStage(p => { const n = { ...p }; delete n[app.id]; return n; })}>Cancel</Button>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}
                                                    <div className="d-flex gap-2 flex-wrap">
                                                        {!isTerminal && nextStage && nextStage !== 'Rejected' && (
                                                            <Button size="sm" variant="outline-success" style={{ fontSize: 12, borderRadius: 8 }} disabled={isMoving} onClick={() => moveStage(app.id, nextStage)}>
                                                                {isMoving ? <Spinner size="sm" /> : <><ArrowRight size={12} className="me-1" />Move to {nextStage}</>}
                                                            </Button>
                                                        )}
                                                        {['Screening','Shortlisted','Interview','Technical','HR Round'].includes(app.stage) && (
                                                            <Button size="sm" variant="outline-primary" style={{ fontSize: 12, borderRadius: 8 }} onClick={() => setScheduleApp(app)}>
                                                                <Calendar size={12} className="me-1" />Schedule Interview
                                                            </Button>
                                                        )}
                                                        {/* FEATURE: Generate Offer Letter — shown for applications at
                                                            the Offer stage, the natural point in the pipeline where
                                                            HR would actually issue one */}
                                                        {app.stage === 'Offer' && (
                                                            <Button size="sm" variant="outline-success" style={{ fontSize: 12, borderRadius: 8 }}
                                                                onClick={() => setOfferLetterApp(app)}>
                                                                <FileSignature size={12} className="me-1" />Generate Offer Letter
                                                            </Button>
                                                        )}
                                                        {full.resume && (
                                                            <Button size="sm" variant="outline-secondary" as="a"
                                                                href={`${process.env.REACT_APP_API_URL?.replace('/api/v1', '')}/${full.resume}`}
                                                                target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, borderRadius: 8 }}>
                                                                <Download size={12} className="me-1" />Resume
                                                            </Button>
                                                        )}
                                                        {!isTerminal && (
                                                            <Button size="sm" variant="outline-danger" style={{ fontSize: 12, borderRadius: 8 }} onClick={() => setRejectApp(app)}>
                                                                <XCircle size={12} className="me-1" />Reject
                                                            </Button>
                                                        )}
                                                        {app.stage === 'Hired'    && <Badge bg="success" className="d-flex align-items-center" style={{ fontSize: 12 }}>✓ Hired</Badge>}
                                                        {app.stage === 'Rejected' && <Badge bg="danger"  className="d-flex align-items-center" style={{ fontSize: 12 }}>✗ Rejected</Badge>}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer className="border-0 pt-2"><Button variant="light" onClick={onClose}>Close</Button></Modal.Footer>
            </Modal>
            <ConfirmModal show={!!rejectApp} title="Reject Application"
                message={`Reject ${fullName}'s application for "${rejectApp?.job_title}"? This cannot be undone easily.`}
                confirmLabel="Yes, Reject" confirmVariant="danger" loading={rejectLoading}
                onConfirm={handleReject} onCancel={() => setRejectApp(null)} />
            <ScheduleInterviewModal show={!!scheduleApp} application={scheduleApp} candidateName={fullName}
                onClose={() => setScheduleApp(null)} onDone={() => { setScheduleApp(null); refresh(); }} />
            {/* FEATURE: Generate Offer Letter modal */}
            <GenerateOfferLetterModal
                show={!!offerLetterApp}
                candidate={cand}
                application={offerLetterApp}
                onClose={() => setOfferLetterApp(null)}
            />
        </>
    );
};
// ════════════════════════════════════════════════════════════════════════════
// ATS PAGE SHELL — unchanged
// ════════════════════════════════════════════════════════════════════════════
const ATSPage = () => {
    const { user } = useAuth();
    const [tab, setTab]             = useState('dashboard');
    const [selectedJob, setSelectedJob] = useState(null);
    const tabs = [
        { key: 'dashboard',  label: 'Dashboard',  icon: BarChart2  },
        { key: 'jobs',       label: 'Jobs',        icon: Briefcase  },
        { key: 'pipeline',   label: 'Pipeline',    icon: ArrowRight },
        { key: 'candidates', label: 'Candidates',  icon: Users      },
        { key: 'interviews', label: 'Interviews',  icon: Calendar   },
    ];
    return (
        <div className="p-2 p-md-4">
            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
                <div>
                    <h4 className="fw-bold mb-1"><Briefcase size={22} className="me-2 text-primary" />Applicant Tracking System</h4>
                    <p className="text-muted small mb-0">Jobs · Pipeline · Candidates · Interviews</p>
                </div>
            </div>
            <div className="d-flex gap-1 flex-wrap mb-4">
                {tabs.map(t => { const Icon = t.icon; return (
                    <Button key={t.key} size="sm" variant={tab === t.key ? 'primary' : 'outline-secondary'} onClick={() => setTab(t.key)}>
                        <Icon size={13} className="me-1" />{t.label}
                    </Button>
                ); })}
            </div>
            {tab === 'dashboard'  && <DashboardTab onViewPipeline={(j) => { setSelectedJob(j); setTab('pipeline'); }} />}
            {tab === 'jobs'       && <JobsTab onViewPipeline={(j) => { setSelectedJob(j); setTab('pipeline'); }} />}
            {tab === 'pipeline'   && <PipelineTab selectedJob={selectedJob} onSelectJob={setSelectedJob} />}
            {tab === 'candidates' && <CandidatesTab />}
            {tab === 'interviews' && <InterviewsTab />}
        </div>
    );
};
// ════════════════════════════════════════════════════════════════════════════
// DASHBOARD TAB — unchanged
// ════════════════════════════════════════════════════════════════════════════
const DashboardTab = ({ onViewPipeline }) => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    useEffect(() => { axiosInstance.get('/ats/stats').then(r => setStats(r.data.data)).catch(() => {}).finally(() => setLoading(false)); }, []);
    if (loading) return <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>;
    if (!stats)  return null;
    const { jobs = {}, applications = {}, top_jobs = [], recent_applications = [] } = stats;
    return (
        <>
            <Row className="g-3 mb-4">
                {[
                    { label: 'Open Positions',   val: jobs.open_jobs || 0,                  color: '#4f46e5' },
                    { label: 'Total Applicants', val: applications.total_applications || 0, color: '#0891b2' },
                    { label: 'In Interview',     val: applications.interviewing || 0,        color: '#d97706' },
                    { label: 'Hired This Cycle', val: applications.hired || 0,              color: '#059669' },
                ].map((k, i) => (
                    <Col key={i} xs={6} md={3}>
                        <Card className="border-0 shadow-sm p-4 text-center">
                            <div style={{ fontSize: 32, fontWeight: 800, color: k.color }}>{k.val}</div>
                            <div className="text-muted small">{k.label}</div>
                        </Card>
                    </Col>
                ))}
            </Row>
            <Row className="g-4 mb-4">
                <Col lg={5}>
                    <Card className="border-0 shadow-sm p-4 h-100">
                        <div className="fw-bold mb-3">Hiring Funnel</div>
                        {[
                            { stage: 'Applied',      count: applications.applied      || 0, color: '#64748b' },
                            { stage: 'Shortlisted',  count: applications.shortlisted  || 0, color: '#4f46e5' },
                            { stage: 'Interviewing', count: applications.interviewing || 0, color: '#d97706' },
                            { stage: 'Offered',      count: applications.offered      || 0, color: '#0891b2' },
                            { stage: 'Hired',        count: applications.hired        || 0, color: '#059669' },
                        ].map((s, i) => (
                            <div key={i} className="mb-3">
                                <div className="d-flex justify-content-between small mb-1"><span>{s.stage}</span><span className="fw-bold">{s.count}</span></div>
                                <div style={{ height: 10, background: '#f1f5f9', borderRadius: 6 }}>
                                    <div style={{ height: '100%', borderRadius: 6, background: s.color, width: `${Math.min(100, applications.total_applications > 0 ? (s.count / applications.total_applications) * 100 : 0)}%`, transition: 'width 0.4s' }} />
                                </div>
                            </div>
                        ))}
                    </Card>
                </Col>
                <Col lg={7}>
                    <Card className="border-0 shadow-sm p-4 h-100">
                        <div className="fw-bold mb-3">Top Positions by Applicants</div>
                        {top_jobs.length === 0 ? <div className="text-muted small">No jobs yet.</div> : top_jobs.map((j, i) => (
                            <div key={i} className="d-flex justify-content-between align-items-center mb-3 pb-2" style={{ borderBottom: '1px solid #f1f5f9' }}>
                                <div><div className="fw-medium small">{j.title}</div><Badge bg={j.status === 'open' ? 'success' : 'secondary'} style={{ fontSize: 9 }}>{j.status}</Badge></div>
                                <div className="text-end"><div className="fw-bold text-primary">{j.applicants}</div><div className="text-muted" style={{ fontSize: 10 }}>{j.hires} hired</div></div>
                            </div>
                        ))}
                    </Card>
                </Col>
            </Row>
            {recent_applications.length > 0 && (
                <Card className="border-0 shadow-sm">
                    <Card.Header className="bg-white fw-bold border-0 pt-3">Recent Applications</Card.Header>
                    <Table hover responsive className="mb-0 small align-middle">
                        <thead className="bg-light">
                            <tr className="text-muted text-uppercase" style={{ fontSize: 10 }}>
                                <th className="ps-4">Candidate</th><th>Position</th><th>Stage</th><th className="pe-4">Applied</th>
                            </tr>
                        </thead>
                        <tbody>
                            {recent_applications.map((a, i) => (
                                <tr key={i}>
                                    <td className="ps-4 fw-medium">{a.candidate_name}</td>
                                    <td>{a.job_title}</td>
                                    <td><Badge bg={STAGE_COLOURS[a.stage] || 'secondary'} style={{ fontSize: 10 }}>{a.stage}</Badge></td>
                                    <td className="pe-4 text-muted">{fmtDate(a.applied_at)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Card>
            )}
        </>
    );
};
// ════════════════════════════════════════════════════════════════════════════
// JOBS TAB + JOB FORM MODAL (with JD Templates picker)
// ════════════════════════════════════════════════════════════════════════════
const JobsTab = ({ onViewPipeline }) => {
    const [jobs, setJobs]        = useState([]);
    const [loading, setLoading]  = useState(true);
    const [filter, setFilter]    = useState('');
    const [showForm, setShowForm]= useState(false);
    const [editing, setEditing]  = useState(null);
    const [confirm, setConfirm]  = useState({ show: false, id: null, title: '', loading: false });
    const load = () => { setLoading(true); axiosInstance.get('/ats/jobs').then(r => setJobs(r.data.data || [])).catch(() => {}).finally(() => setLoading(false)); };
    useEffect(load, []);
    const toggleStatus = async (job) => {
        const next = job.status === 'open' ? 'closed' : 'open';
        try { await axiosInstance.patch(`/ats/jobs/${job.id}/status`, { status: next }); toast.success(`Job ${next}`); load(); } catch { toast.error('Failed'); }
    };
    const handleDeleteConfirm = async () => {
        setConfirm(p => ({ ...p, loading: true }));
        try { await axiosInstance.delete(`/ats/jobs/${confirm.id}`); toast.success('Job deleted'); load(); } catch { toast.error('Delete failed'); }
        finally { setConfirm({ show: false, id: null, title: '', loading: false }); }
    };
    const filtered = jobs.filter(j => !filter || j.title?.toLowerCase().includes(filter.toLowerCase()));
    return (
        <>
            <div className="d-flex gap-2 mb-3 align-items-center">
                <InputGroup size="sm" style={{ maxWidth: 260 }}>
                    <InputGroup.Text><Search size={13} /></InputGroup.Text>
                    <Form.Control placeholder="Search jobs…" value={filter} onChange={e => setFilter(e.target.value)} />
                </InputGroup>
                <Button size="sm" variant="primary" onClick={() => { setEditing(null); setShowForm(true); }}><Plus size={14} className="me-1" />Post Job</Button>
                <Button size="sm" variant="outline-secondary" className="ms-auto" onClick={load}><RefreshCw size={13} /></Button>
            </div>
            {loading ? <div className="text-center py-4"><Spinner animation="border" /></div> : (
                <Row className="g-3">
                    {filtered.length === 0 && (
                        <Col xs={12}><Card className="border-0 shadow-sm p-5 text-center text-muted"><Briefcase size={40} className="mx-auto mb-2 opacity-30" /><p>No jobs yet. Post your first opening!</p></Card></Col>
                    )}
                    {filtered.map(j => (
                        <Col key={j.id} xs={12} md={6} lg={4}>
                            <Card className="border-0 shadow-sm h-100 p-4">
                                <div className="d-flex justify-content-between mb-2">
                                    <Badge bg={j.status === 'open' ? 'success' : j.status === 'draft' ? 'secondary' : 'danger'}>{j.status}</Badge>
                                    <span className="text-muted small">{j.employment_type}</span>
                                </div>
                                <h6 className="fw-bold mb-1">{j.title}</h6>
                                <div className="text-muted small mb-3">{j.location || 'Remote'} · {j.category_name || 'General'}</div>
                                {(j.salary_min || j.salary_max) && <div className="text-success small mb-2">{fmtINR(j.salary_min)} – {fmtINR(j.salary_max)} p.a.</div>}
                                <div className="d-flex gap-3 mb-3" style={{ fontSize: 12 }}>
                                    <span><strong>{j.total_applicants || 0}</strong> applied</span>
                                    <span><strong>{j.shortlisted || 0}</strong> shortlisted</span>
                                    <span><strong>{j.hired || 0}</strong> hired</span>
                                </div>
                                <div className="d-flex gap-1 mt-auto flex-wrap">
                                    <Button size="sm" variant="outline-primary" className="py-0" onClick={() => onViewPipeline(j)}>Pipeline <ChevronRight size={12} /></Button>
                                    <Button size="sm" variant="outline-secondary" className="py-0" onClick={() => { setEditing(j); setShowForm(true); }}><Edit2 size={12} /></Button>
                                    <Button size="sm" variant={j.status === 'open' ? 'outline-warning' : 'outline-success'} className="py-0" onClick={() => toggleStatus(j)}>{j.status === 'open' ? 'Close' : 'Open'}</Button>
                                    <Button size="sm" variant="outline-danger" className="py-0" onClick={() => setConfirm({ show: true, id: j.id, title: j.title, loading: false })}><Trash2 size={12} /></Button>
                                </div>
                            </Card>
                        </Col>
                    ))}
                </Row>
            )}
            <JobFormModal show={showForm} job={editing} onClose={() => setShowForm(false)} onDone={() => { setShowForm(false); load(); }} />
            <ConfirmModal show={confirm.show} title="Delete Job" message={`Delete "${confirm.title}"? All applications will be removed.`}
                onConfirm={handleDeleteConfirm} onCancel={() => setConfirm({ show: false, id: null, title: '', loading: false })} loading={confirm.loading} />
        </>
    );
};
const validateJob = (form) => {
    const e = {};
    const title = form.title?.trim();
    if (!title)              e.title = 'Job title is required';
    else if (title.length < 2)   e.title = 'Title must be at least 2 characters';
    else if (title.length > 200) e.title = 'Title cannot exceed 200 characters';
    const min = parseFloat(form.salary_min); const max = parseFloat(form.salary_max);
    if (form.salary_min !== '' && !isNaN(min) && min < 0) e.salary_min = 'Salary cannot be negative';
    if (form.salary_max !== '' && !isNaN(max) && max < 0) e.salary_max = 'Salary cannot be negative';
    if (form.salary_min !== '' && form.salary_max !== '' && !isNaN(min) && !isNaN(max) && max <= min)
        e.salary_max = 'Maximum must be greater than minimum';
    return e;
};
const JobFormModal = ({ show, job, onClose, onDone }) => {
    const isNew = !job?.id;
    const [form, setForm] = useState({ title: '', description: '', employment_type: 'Full-time', location: '', salary_min: '', salary_max: '', status: 'open' });
    const [errors, setErrors]         = useState({});
    const [attempted, setAttempted]   = useState(false);
    const [saving, setSaving]         = useState(false);
    const [showTemplates, setShowTemplates] = useState(false); // FEATURE
    useEffect(() => {
        if (job) setForm({ title: job.title || '', description: job.description || '', employment_type: job.employment_type || 'Full-time', location: job.location || '', salary_min: job.salary_min || '', salary_max: job.salary_max || '', status: job.status || 'open' });
        else     setForm({ title: '', description: '', employment_type: 'Full-time', location: '', salary_min: '', salary_max: '', status: 'open' });
        setErrors({}); setAttempted(false);
    }, [job, show]);
    const setField = (k, v) => { setForm(p => ({ ...p, [k]: v })); if (attempted) setErrors(prev => { const n = { ...prev }; delete n[k]; return n; }); };
    // FEATURE: apply selected template
    const handleTemplateSelect = ({ title, description, employment_type }) => {
        setForm(p => ({ ...p, title, description, employment_type }));
        setErrors(prev => { const n = { ...prev }; delete n.title; delete n.description; return n; });
        toast.success(`Template "${title}" applied — edit as needed before posting.`);
    };
    const save = async () => {
        setAttempted(true);
        const errs = validateJob(form);
        if (Object.keys(errs).length > 0) { setErrors(errs); return; }
        setSaving(true);
        try {
            if (isNew) await axiosInstance.post('/ats/jobs', form);
            else       await axiosInstance.put(`/ats/jobs/${job.id}`, form);
            toast.success(isNew ? 'Job posted!' : 'Job updated!');
            onDone();
        } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
        finally { setSaving(false); }
    };
    return (
        <>
            <Modal show={show} onHide={onClose} centered size="lg">
                <Modal.Header closeButton className="border-0">
                    <Modal.Title className="fw-bold fs-6">{isNew ? 'Post New Job' : `Edit: ${job?.title}`}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Row className="g-3">
                        <Col xs={8}>
                            {/* FEATURE: Use Template button beside Job Title label */}
                            <div className="d-flex align-items-center justify-content-between mb-1">
                                <Form.Label className="small fw-bold mb-0">Job Title *</Form.Label>
                                <Button size="sm" variant="outline-primary"
                                    style={{ fontSize: 11, padding: '2px 10px', borderRadius: 8 }}
                                    onClick={() => setShowTemplates(true)}>
                                    📋 Use Template
                                </Button>
                            </div>
                            <Form.Control placeholder="e.g. Senior React Developer" value={form.title} maxLength={200}
                                isInvalid={attempted && !!errors.title} onChange={e => setField('title', e.target.value)} />
                            <Form.Control.Feedback type="invalid">{errors.title}</Form.Control.Feedback>
                        </Col>
                        <Col xs={4}>
                            <Form.Label className="small fw-bold">Status</Form.Label>
                            <Form.Select value={form.status} onChange={e => setField('status', e.target.value)}>
                                <option value="draft">Draft</option><option value="open">Open</option><option value="closed">Closed</option>
                            </Form.Select>
                        </Col>
                        <Col xs={6}>
                            <Form.Label className="small fw-bold">Employment Type</Form.Label>
                            <Form.Select value={form.employment_type} onChange={e => setField('employment_type', e.target.value)}>
                                {['Full-time','Part-time','Contract','Internship','Remote'].map(t => <option key={t}>{t}</option>)}
                            </Form.Select>
                        </Col>
                        <Col xs={6}>
                            <Form.Label className="small fw-bold">Location</Form.Label>
                            <Form.Control placeholder="e.g. Tirupati / Remote" value={form.location} onChange={e => setField('location', e.target.value)} />
                        </Col>
                        <Col xs={6}>
                            <Form.Label className="small fw-bold">Salary Min (₹ p.a.)</Form.Label>
                            <Form.Control type="number" min="0" placeholder="e.g. 800000" value={form.salary_min}
                                isInvalid={attempted && !!errors.salary_min} onChange={e => setField('salary_min', e.target.value)} />
                            <Form.Control.Feedback type="invalid">{errors.salary_min}</Form.Control.Feedback>
                        </Col>
                        <Col xs={6}>
                            <Form.Label className="small fw-bold">Salary Max (₹ p.a.)</Form.Label>
                            <Form.Control type="number" min="0" placeholder="e.g. 1500000" value={form.salary_max}
                                isInvalid={attempted && !!errors.salary_max} onChange={e => setField('salary_max', e.target.value)} />
                            <Form.Control.Feedback type="invalid">{errors.salary_max}</Form.Control.Feedback>
                        </Col>
                        <Col xs={12}>
                            <Form.Label className="small fw-bold">Job Description</Form.Label>
                            <Form.Control as="textarea" rows={7}
                                placeholder="Role overview, responsibilities, requirements… or click 'Use Template' above to auto-fill."
                                value={form.description} onChange={e => setField('description', e.target.value)} />
                            {form.description && <div className="text-muted mt-1" style={{ fontSize: 11 }}>{form.description.length} characters</div>}
                        </Col>
                    </Row>
                </Modal.Body>
                <Modal.Footer className="border-0">
                    <Button variant="light" onClick={onClose}>Cancel</Button>
                    <Button variant="primary" onClick={save} disabled={saving}>
                        {saving ? <Spinner size="sm" /> : isNew ? '📋 Post Job' : '💾 Save Changes'}
                    </Button>
                </Modal.Footer>
            </Modal>
            {/* FEATURE: JD Templates Picker */}
            <JDTemplatesModal show={showTemplates} onClose={() => setShowTemplates(false)} onSelect={handleTemplateSelect} />
        </>
    );
};
// ════════════════════════════════════════════════════════════════════════════
// PIPELINE TAB — unchanged
// ════════════════════════════════════════════════════════════════════════════
const PipelineTab = ({ selectedJob, onSelectJob }) => {
    const [jobs, setJobs]               = useState([]);
    const [pipeline, setPipeline]       = useState(null);
    const [loading, setLoading]         = useState(false);
    const [selected, setSelected]       = useState(null);
    const [activeStage, setActiveStage] = useState('Applied');
    const [addApp, setAddApp]           = useState(false);
    const [movingApp, setMovingApp]     = useState(null);
    const [showRejected, setShowRejected]= useState(false);
    const [scheduleApp, setScheduleApp] = useState(null);
    useEffect(() => { axiosInstance.get('/ats/jobs').then(r => setJobs(r.data.data || [])).catch(() => {}); }, []);
    useEffect(() => {
        const job = selectedJob || selected;
        if (job?.id) loadPipeline(job.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedJob, selected]);
    const loadPipeline = async (jobId) => {
        setLoading(true);
        try { const res = await axiosInstance.get(`/ats/pipeline/${jobId}`); setPipeline(res.data.data); }
        catch { toast.error('Failed to load pipeline'); }
        finally { setLoading(false); }
    };
    const moveStage = async (appId, newStage) => {
        setMovingApp(appId);
        try { await axiosInstance.patch(`/ats/applications/${appId}/stage`, { stage: newStage }); toast.success(`Moved to ${newStage}`); const job = selectedJob || selected; if (job?.id) loadPipeline(job.id); }
        catch { toast.error('Failed'); }
        finally { setMovingApp(null); }
    };
    const currentJob    = selectedJob || selected;
    const counts        = pipeline ? PIPELINE_STAGES.reduce((acc, s) => { acc[s] = (pipeline.kanban?.[s] || []).length; return acc; }, {}) : {};
    const totalActive   = ACTIVE_STAGES.reduce((a, s) => a + (counts[s] || 0), 0);
    const totalRejected = counts['Rejected'] || 0;
    const stageCards    = pipeline?.kanban?.[activeStage] || [];
    const rejectedCards = pipeline?.kanban?.['Rejected']  || [];
    return (
        <>
            <div className="d-flex gap-2 mb-4 align-items-center flex-wrap">
                <Form.Select size="sm" style={{ maxWidth: 320 }} value={currentJob?.id || ''}
                    onChange={e => { const j = jobs.find(j => j.id === parseInt(e.target.value)); setSelected(j); onSelectJob(j); setActiveStage('Applied'); }}>
                    <option value="">— Select a job to view pipeline —</option>
                    {jobs.map(j => <option key={j.id} value={j.id}>{j.title}{j.status !== 'open' ? ` (${j.status})` : ''}</option>)}
                </Form.Select>
                {currentJob && (
                    <>
                        <Button size="sm" variant="primary" onClick={() => setAddApp(true)}><Plus size={14} className="me-1" />Add Candidate</Button>
                        <Button size="sm" variant="outline-secondary" onClick={() => loadPipeline(currentJob.id)}><RefreshCw size={13} /></Button>
                        {pipeline && <span className="text-muted small">{totalActive} active · {totalRejected} rejected</span>}
                    </>
                )}
            </div>
            {!currentJob ? (
                <Card className="border-0 shadow-sm" style={{ borderRadius: 16 }}>
                    <Card.Body className="text-center py-5 text-muted">
                        <div style={{ fontSize: 52, marginBottom: 12 }}>📋</div>
                        <h6 className="fw-semibold mb-1">Select a Job</h6>
                        <p className="small mb-0">Choose a job above to view the recruitment pipeline.</p>
                    </Card.Body>
                </Card>
            ) : loading ? (
                <div className="text-center py-5"><Spinner animation="border" variant="primary" /><p className="text-muted small mt-2">Loading…</p></div>
            ) : (
                <>
                    <div className="d-flex align-items-center gap-3 p-3 rounded-3 mb-4" style={{ background: 'linear-gradient(135deg,#4f46e5,#7c3aed)', color: '#fff' }}>
                        <div style={{ flex: 1 }}>
                            <div className="fw-bold" style={{ fontSize: 16 }}>{currentJob.title}</div>
                            <div style={{ fontSize: 12, opacity: 0.8 }}>{currentJob.location || 'Remote'} · {currentJob.employment_type || 'Full-time'}</div>
                        </div>
                        <div className="d-flex gap-4">
                            {[{ label: 'Total', val: totalActive + totalRejected }, { label: 'Active', val: totalActive }, { label: 'Offered', val: counts['Offer'] || 0 }, { label: 'Hired', val: counts['Hired'] || 0 }].map(({ label, val }) => (
                                <div key={label} className="text-center">
                                    <div className="fw-bold" style={{ fontSize: 20 }}>{val}</div>
                                    <div style={{ fontSize: 10, opacity: 0.75 }}>{label}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="mb-4" style={{ overflowX: 'auto' }}>
                        <div style={{ display: 'flex', alignItems: 'stretch', minWidth: ACTIVE_STAGES.length * 108, background: '#fff', borderRadius: 14, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', overflow: 'hidden', border: '1px solid #e5e7eb' }}>
                            {ACTIVE_STAGES.map((stage, idx) => {
                                const isActive = stage === activeStage;
                                const isPast   = ACTIVE_STAGES.indexOf(activeStage) > idx;
                                const isLast   = idx === ACTIVE_STAGES.length - 1;
                                const count    = counts[stage] || 0;
                                const hex      = STAGE_HEX[stage] || '#64748B';
                                return (
                                    <div key={stage} style={{ flex: 1, position: 'relative', minWidth: 100 }}>
                                        {!isLast && (
                                            <div style={{ position: 'absolute', right: -13, top: '50%', transform: 'translateY(-50%)', zIndex: 2, width: 26, height: 26, borderRadius: '50%', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 0 0 2px #e5e7eb' }}>
                                                <ArrowRight size={12} color="#94a3b8" />
                                            </div>
                                        )}
                                        <button onClick={() => setActiveStage(stage)} style={{ width: '100%', border: 'none', padding: '14px 8px 12px', cursor: 'pointer', textAlign: 'center', background: isActive ? hex : isPast ? `${hex}12` : '#fafafa', borderBottom: isActive ? `3px solid ${hex}` : '3px solid transparent', transition: 'all 0.18s' }}>
                                            <div style={{ width: 32, height: 32, borderRadius: '50%', background: isActive ? 'rgba(255,255,255,0.22)' : isPast ? hex : '#e5e7eb', color: isActive ? '#fff' : isPast ? '#fff' : '#9ca3af', fontWeight: 700, fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 6px', border: isActive ? '2px solid rgba(255,255,255,0.35)' : isPast ? `2px solid ${hex}` : '2px solid #e5e7eb' }}>
                                                {isPast ? '✓' : idx + 1}
                                            </div>
                                            <div style={{ fontSize: 10, fontWeight: 700, color: isActive ? '#fff' : isPast ? hex : '#6b7280', marginBottom: 4, whiteSpace: 'nowrap' }}>{stage}</div>
                                            <div style={{ display: 'inline-block', background: isActive ? 'rgba(255,255,255,0.22)' : count > 0 ? `${hex}20` : '#f1f5f9', color: isActive ? '#fff' : count > 0 ? hex : '#94a3b8', borderRadius: 20, padding: '1px 9px', fontSize: 11, fontWeight: 700 }}>{count}</div>
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                    <div className="d-flex align-items-center justify-content-between mb-3">
                        <div>
                            <span className="fw-bold" style={{ color: STAGE_HEX[activeStage], fontSize: 15 }}>{activeStage}</span>
                            <span className="text-muted small ms-2">— {stageCards.length} candidate{stageCards.length !== 1 ? 's' : ''}</span>
                        </div>
                    </div>
                    {stageCards.length === 0 ? (
                        <Card className="border-0 mb-4" style={{ background: STAGE_BG[activeStage]?.col || '#f8fafc', borderRadius: 12 }}>
                            <Card.Body className="text-center py-4 text-muted">
                                <div style={{ fontSize: 36, marginBottom: 8 }}>👤</div>
                                <div className="small">No candidates at <strong>{activeStage}</strong> stage.</div>
                            </Card.Body>
                        </Card>
                    ) : (
                        <Row className="g-3 mb-4">
                            {stageCards.map(app => {
                                const isMovingThis = movingApp === app.id;
                                const stIdx        = PIPELINE_STAGES.indexOf(activeStage);
                                const nextStage    = PIPELINE_STAGES[stIdx + 1];
                                const bg           = STAGE_BG[activeStage] || { col: '#f8fafc', hdr: '#64748B', border: '#e5e7eb' };
                                return (
                                    <Col key={app.id} xs={12} sm={6} lg={4}>
                                        <Card className="border-0 shadow-sm h-100" style={{ borderRadius: 12, borderLeft: `4px solid ${bg.hdr}` }}>
                                            <Card.Body className="p-3">
                                                <div className="d-flex align-items-center gap-2 mb-3">
                                                    <AvatarInitials name={app.candidate_name || ''} size={38} bgColor={bg.hdr} />
                                                    <div style={{ minWidth: 0 }}>
                                                        <div className="fw-semibold text-truncate" style={{ fontSize: 14 }}>{app.candidate_name}</div>
                                                        <div className="text-muted text-truncate" style={{ fontSize: 11 }}>{app.candidate_email}</div>
                                                    </div>
                                                </div>
                                                <div className="d-flex gap-3 mb-3" style={{ fontSize: 11, color: '#6b7280' }}>
                                                    <span><Clock size={11} className="me-1" />{fmtDate(app.applied_at)}</span>
                                                </div>
                                                <div className="d-flex gap-1 flex-wrap">
                                                    {activeStage !== 'Hired' && nextStage && nextStage !== 'Rejected' && (
                                                        <Button size="sm" variant="success" style={{ fontSize: 11, borderRadius: 8, flex: 1 }} disabled={isMovingThis} onClick={() => moveStage(app.id, nextStage)}>
                                                            {isMovingThis ? <Spinner size="sm" /> : <><ArrowRight size={11} className="me-1" />→ {nextStage}</>}
                                                        </Button>
                                                    )}
                                                    {['Screening','Shortlisted','Interview','Technical','HR Round'].includes(activeStage) && (
                                                        <Button size="sm" variant="outline-primary" style={{ fontSize: 11, borderRadius: 8 }} onClick={() => setScheduleApp(app)}>
                                                            <Calendar size={11} />
                                                        </Button>
                                                    )}
                                                    {activeStage !== 'Hired' && (
                                                        <Dropdown>
                                                            <Dropdown.Toggle size="sm" variant="outline-secondary" style={{ fontSize: 11, borderRadius: 8 }} disabled={isMovingThis}>⋯</Dropdown.Toggle>
                                                            <Dropdown.Menu style={{ fontSize: 12 }}>
                                                                <Dropdown.Header style={{ fontSize: 10 }}>MOVE TO</Dropdown.Header>
                                                                {PIPELINE_STAGES.map(s => (
                                                                    <Dropdown.Item key={s} active={s === activeStage} onClick={() => moveStage(app.id, s)} style={{ fontSize: 12 }}>
                                                                        <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', marginRight: 8, background: STAGE_HEX[s] || '#64748B' }} />{s}
                                                                    </Dropdown.Item>
                                                                ))}
                                                            </Dropdown.Menu>
                                                        </Dropdown>
                                                    )}
                                                    {activeStage !== 'Hired' && activeStage !== 'Rejected' && (
                                                        <Button size="sm" variant="outline-danger" style={{ fontSize: 11, borderRadius: 8 }} disabled={isMovingThis} onClick={() => moveStage(app.id, 'Rejected')}>✗</Button>
                                                    )}
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                );
                            })}
                        </Row>
                    )}
                    {totalRejected > 0 && (
                        <div>
                            <Button size="sm" variant="outline-danger" className="mb-3" onClick={() => setShowRejected(p => !p)} style={{ borderRadius: 8 }}>
                                <XCircle size={13} className="me-1" />{showRejected ? 'Hide' : 'Show'} Rejected ({totalRejected})
                            </Button>
                            {showRejected && (
                                <Row className="g-3">
                                    {rejectedCards.map(app => (
                                        <Col key={app.id} xs={12} sm={6} lg={4}>
                                            <Card className="border-0 shadow-sm" style={{ borderRadius: 12, borderLeft: '4px solid #DC2626', opacity: 0.72 }}>
                                                <Card.Body className="p-3">
                                                    <div className="d-flex align-items-center gap-2 mb-2">
                                                        <AvatarInitials name={app.candidate_name || ''} size={32} bgColor="#DC2626" />
                                                        <div style={{ minWidth: 0 }}>
                                                            <div className="fw-semibold text-truncate" style={{ fontSize: 13 }}>{app.candidate_name}</div>
                                                            <div className="text-muted text-truncate" style={{ fontSize: 11 }}>{app.candidate_email}</div>
                                                        </div>
                                                    </div>
                                                    <div className="d-flex gap-2 align-items-center">
                                                        <Badge bg="danger" style={{ fontSize: 10 }}>✗ Rejected</Badge>
                                                        <Button size="sm" variant="outline-secondary" style={{ fontSize: 10, padding: '1px 8px', borderRadius: 6 }} onClick={() => moveStage(app.id, 'Applied')}>Restore</Button>
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    ))}
                                </Row>
                            )}
                        </div>
                    )}
                </>
            )}
            {addApp && currentJob && (
                <AddCandidateModal jobId={currentJob.id} jobTitle={currentJob.title}
                    onClose={() => setAddApp(false)} onDone={() => { setAddApp(false); loadPipeline(currentJob.id); }} />
            )}
            <ScheduleInterviewModal show={!!scheduleApp} application={scheduleApp} candidateName={scheduleApp?.candidate_name || ''}
                onClose={() => setScheduleApp(null)} onDone={() => { setScheduleApp(null); if (currentJob?.id) loadPipeline(currentJob.id); }} />
        </>
    );
};
// ── Add Candidate Modal — unchanged ──────────────────────────────────────────
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^[+]?[\d\s\-().]{7,20}$/;
const validateCandidate = (form) => {
    const e = {};
    if (!form.first_name?.trim() && !form.last_name?.trim()) e.first_name = 'Enter at least a first name or last name';
    const email = form.email?.trim();
    if (!email) e.email = 'Email address is required';
    else if (!EMAIL_REGEX.test(email)) e.email = 'Enter a valid email address';
    if (form.phone?.trim() && !PHONE_REGEX.test(form.phone.trim())) e.phone = 'Enter a valid phone number';
    if (form.resume?.trim() && !form.resume.trim().startsWith('http')) e.resume = 'Resume URL must start with http';
    return e;
};
const AddCandidateModal = ({ jobId, jobTitle, onClose, onDone }) => {
    const [form, setForm]           = useState({ first_name: '', last_name: '', email: '', phone: '', resume: '' });
    const [errors, setErrors]       = useState({});
    const [attempted, setAttempted] = useState(false);
    const [saving, setSaving]       = useState(false);
    const setField = (k, v) => { setForm(p => ({ ...p, [k]: v })); if (attempted) setErrors(prev => { const n = { ...prev }; delete n[k]; return n; }); };
    const save = async () => {
        setAttempted(true); const errs = validateCandidate(form);
        if (Object.keys(errs).length > 0) { setErrors(errs); return; }
        setSaving(true);
        try { await axiosInstance.post(`/ats/apply/${jobId}`, form); toast.success('Candidate added!'); onDone(); }
        catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
        finally { setSaving(false); }
    };
    return (
        <Modal show onHide={onClose} centered>
            <Modal.Header closeButton className="border-0"><Modal.Title className="fw-bold fs-6">Add Candidate — {jobTitle}</Modal.Title></Modal.Header>
            <Modal.Body>
                <Row className="g-3">
                    <Col xs={6}><Form.Label className="small fw-bold">First Name</Form.Label><Form.Control value={form.first_name} isInvalid={attempted && !!errors.first_name} onChange={e => setField('first_name', e.target.value)} autoFocus /><Form.Control.Feedback type="invalid">{errors.first_name}</Form.Control.Feedback></Col>
                    <Col xs={6}><Form.Label className="small fw-bold">Last Name</Form.Label><Form.Control value={form.last_name} onChange={e => setField('last_name', e.target.value)} /></Col>
                    <Col xs={12}><Form.Label className="small fw-bold">Email *</Form.Label><Form.Control type="email" value={form.email} isInvalid={attempted && !!errors.email} onChange={e => setField('email', e.target.value)} /><Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback></Col>
                    <Col xs={6}><Form.Label className="small fw-bold">Phone</Form.Label><Form.Control value={form.phone} isInvalid={attempted && !!errors.phone} onChange={e => setField('phone', e.target.value)} /><Form.Control.Feedback type="invalid">{errors.phone}</Form.Control.Feedback></Col>
                    <Col xs={6}><Form.Label className="small fw-bold">Resume URL</Form.Label><Form.Control placeholder="https://…" value={form.resume} isInvalid={attempted && !!errors.resume} onChange={e => setField('resume', e.target.value)} /><Form.Control.Feedback type="invalid">{errors.resume}</Form.Control.Feedback></Col>
                </Row>
            </Modal.Body>
            <Modal.Footer className="border-0">
                <Button variant="light" onClick={onClose}>Cancel</Button>
                <Button variant="primary" onClick={save} disabled={saving}>{saving ? <Spinner size="sm" /> : 'Add to Pipeline'}</Button>
            </Modal.Footer>
        </Modal>
    );
};
// ════════════════════════════════════════════════════════════════════════════
// CANDIDATES TAB — unchanged
// ════════════════════════════════════════════════════════════════════════════
const CandidatesTab = () => {
    const [candidates, setCandidates] = useState([]);
    const [loading, setLoading]       = useState(true);
    const [search, setSearch]         = useState('');
    const [detail, setDetail]         = useState(null);
    const load = (q = '') => { setLoading(true); axiosInstance.get('/ats/candidates', { params: q ? { q } : {} }).then(r => setCandidates(r.data.data || [])).catch(() => {}).finally(() => setLoading(false)); };
    useEffect(() => { load(); }, []);
    return (
        <>
            <div className="d-flex gap-2 mb-3">
                <InputGroup size="sm" style={{ maxWidth: 280 }}>
                    <InputGroup.Text><Search size={13} /></InputGroup.Text>
                    <Form.Control placeholder="Search by name or email…" value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && load(search)} />
                </InputGroup>
                <Button size="sm" variant="outline-primary" onClick={() => load(search)}>Search</Button>
            </div>
            {loading ? <div className="text-center py-4"><Spinner animation="border" /></div> : (
                <Card className="border-0 shadow-sm">
                    <Table hover responsive className="mb-0 small align-middle">
                        <thead className="bg-light">
                            <tr className="text-muted text-uppercase" style={{ fontSize: 10 }}>
                                <th className="ps-4">Candidate</th><th>Contact</th><th>Applications</th><th>Latest Stage</th><th className="pe-4">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {candidates.length === 0 && <tr><td colSpan={5} className="text-center text-muted py-4">No candidates found.</td></tr>}
                            {candidates.map(c => (
                                <tr key={c.id}>
                                    <td className="ps-4"><div className="d-flex align-items-center gap-2"><AvatarInitials name={`${c.first_name || ''} ${c.last_name || ''}`} size={30} /><div className="fw-medium">{c.first_name} {c.last_name}</div></div></td>
                                    <td><div>{c.email}</div><div className="text-muted" style={{ fontSize: 10 }}>{c.phone}</div></td>
                                    <td>{c.total_applications}</td>
                                    <td>{c.latest_stage && <Badge bg={STAGE_COLOURS[c.latest_stage] || 'secondary'} style={{ fontSize: 10 }}>{c.latest_stage}</Badge>}</td>
                                    <td className="pe-4"><Button size="sm" variant="outline-primary" style={{ fontSize: 11, borderRadius: 8 }} onClick={() => setDetail(c)}><Eye size={12} className="me-1" />View / Edit</Button></td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Card>
            )}
            {detail && <CandidateDetailModal candidate={detail} onClose={() => setDetail(null)} />}
        </>
    );
};
// ════════════════════════════════════════════════════════════════════════════
// STAR RATING — unchanged
// ════════════════════════════════════════════════════════════════════════════
const StarRating = ({ value, onChange, readonly = false, max = 5 }) => (
    <div className="d-flex gap-1">
        {Array.from({ length: max }, (_, i) => i + 1).map(star => (
            <button key={star} type="button" onClick={() => !readonly && onChange && onChange(star)}
                style={{ background: 'none', border: 'none', cursor: readonly ? 'default' : 'pointer', padding: 2, lineHeight: 1 }}>
                <Star size={26} fill={star <= value ? '#F59E0B' : 'none'} color={star <= value ? '#F59E0B' : '#D1D5DB'} strokeWidth={1.5} />
            </button>
        ))}
        {value > 0 && <span className="ms-2 small text-muted align-self-center">{['', 'Poor', 'Fair', 'Good', 'Very Good', 'Excellent'][value]}</span>}
    </div>
);
// ════════════════════════════════════════════════════════════════════════════
// FEEDBACK MODAL — unchanged
// ════════════════════════════════════════════════════════════════════════════
const FeedbackModal = ({ interview, onClose, onDone }) => {
    const [rating, setRating]     = useState(0);
    const [comments, setComments] = useState('');
    const [saving, setSaving]     = useState(false);
    const [ratingErr, setRatingErr]= useState('');
    if (!interview) return null;
    const submit = async () => {
        if (!rating) { setRatingErr('Please select a rating'); return; }
        setRatingErr(''); setSaving(true);
        try {
            await axiosInstance.post(`/ats/interviews/${interview.id}/feedback`, { rating, comments: comments.trim() || null });
            toast.success('Feedback submitted. Interview marked as Completed.');
            onDone(); onClose();
        } catch (err) { toast.error(err.response?.data?.message || 'Failed to submit feedback'); }
        finally { setSaving(false); }
    };
    return (
        <Modal show onHide={onClose} centered size="md" backdrop="static">
            <Modal.Header closeButton className="border-0">
                <div>
                    <Modal.Title className="fw-bold fs-6">Interview Feedback</Modal.Title>
                    <div className="text-muted small mt-1"><strong>{interview.candidate_name}</strong> — {interview.job_title}<Badge className="ms-2" bg="secondary" style={{ fontSize: 10 }}>{interview.mode}</Badge></div>
                </div>
            </Modal.Header>
            <Modal.Body>
                <Row className="g-3">
                    <Col xs={12}>
                        <div className="p-3 rounded" style={{ background: '#f8fafc', border: '1px solid #e5e7eb' }}>
                            <Row className="g-1" style={{ fontSize: 13 }}>
                                <Col xs={6}><span className="text-muted">Candidate:</span> <span className="fw-medium ms-1">{interview.candidate_name}</span></Col>
                                <Col xs={6}><span className="text-muted">Date:</span> <span className="fw-medium ms-1">{fmtDate(interview.scheduled_at)}</span></Col>
                                <Col xs={6}><span className="text-muted">Job:</span> <span className="fw-medium ms-1">{interview.job_title}</span></Col>
                                <Col xs={6}><span className="text-muted">Mode:</span> <span className="fw-medium ms-1">{interview.mode}</span></Col>
                            </Row>
                        </div>
                    </Col>
                    <Col xs={12}>
                        <Form.Label className="small fw-bold">Overall Rating <span className="text-danger">*</span></Form.Label>
                        <StarRating value={rating} onChange={(v) => { setRating(v); setRatingErr(''); }} />
                        {ratingErr && <div className="text-danger mt-1" style={{ fontSize: 12 }}>{ratingErr}</div>}
                    </Col>
                    <Col xs={12}>
                        <Form.Label className="small fw-bold">Comments <span className="text-muted fw-normal">(optional)</span></Form.Label>
                        <Form.Control as="textarea" rows={4} placeholder="Communication skills, technical knowledge, cultural fit…"
                            value={comments} onChange={e => setComments(e.target.value)} style={{ resize: 'vertical' }} />
                        <div className="text-muted mt-1" style={{ fontSize: 11 }}>Submitting will mark this interview as <strong>Completed</strong>.</div>
                    </Col>
                </Row>
            </Modal.Body>
            <Modal.Footer className="border-0 pt-0">
                <Button variant="light" onClick={onClose} disabled={saving}>Cancel</Button>
                <Button onClick={submit} disabled={saving} style={{ background: 'linear-gradient(135deg,#4f46e5,#7c3aed)', border: 'none' }}>
                    {saving ? <><Spinner size="sm" className="me-2" />Submitting…</> : <><CheckCircle size={14} className="me-2" />Submit Feedback</>}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};
// ════════════════════════════════════════════════════════════════════════════
// VIEW FEEDBACK MODAL — unchanged (read-only)
// ════════════════════════════════════════════════════════════════════════════
const ViewFeedbackModal = ({ interview, onClose }) => {
    if (!interview) return null;
    const rating   = parseInt(interview.feedback_rating) || 0;
    const comments = interview.feedback_comments || '';
    return (
        <Modal show onHide={onClose} centered size="md">
            <Modal.Header closeButton className="border-0">
                <div>
                    <Modal.Title className="fw-bold fs-6"><Star size={16} className="me-2 text-warning" fill="#F59E0B" />Interview Feedback</Modal.Title>
                    <div className="text-muted small mt-1"><strong>{interview.candidate_name}</strong> — {interview.job_title}</div>
                </div>
            </Modal.Header>
            <Modal.Body>
                <div className="p-3 rounded mb-4" style={{ background: '#f8fafc', border: '1px solid #e5e7eb' }}>
                    <Row className="g-1" style={{ fontSize: 13 }}>
                        <Col xs={6}><span className="text-muted">Candidate:</span> <span className="fw-medium ms-1">{interview.candidate_name}</span></Col>
                        <Col xs={6}><span className="text-muted">Date:</span> <span className="fw-medium ms-1">{fmtDate(interview.scheduled_at)}</span></Col>
                        <Col xs={6}><span className="text-muted">Interviewer:</span> <span className="fw-medium ms-1">{interview.interviewer_name}</span></Col>
                        <Col xs={6}><span className="text-muted">Mode:</span> <span className="fw-medium ms-1">{interview.mode}</span></Col>
                    </Row>
                </div>
                <div className="mb-4">
                    <div className="small fw-bold text-muted text-uppercase mb-2" style={{ letterSpacing: '0.05em' }}>Overall Rating</div>
                    {rating > 0 ? <StarRating value={rating} readonly /> : <span className="text-muted small">No rating submitted</span>}
                </div>
                <div>
                    <div className="small fw-bold text-muted text-uppercase mb-2" style={{ letterSpacing: '0.05em' }}>Comments</div>
                    {comments ? <div style={{ background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 10, padding: '16px', fontSize: 14, color: '#374151', lineHeight: 1.75, whiteSpace: 'pre-wrap' }}>{comments}</div>
                    : <span className="text-muted small">No comments provided</span>}
                </div>
            </Modal.Body>
            <Modal.Footer className="border-0"><Button variant="light" onClick={onClose}>Close</Button></Modal.Footer>
        </Modal>
    );
};
// ════════════════════════════════════════════════════════════════════════════
// INTERVIEWS TAB — unchanged
// ════════════════════════════════════════════════════════════════════════════
const InterviewsTab = () => {
    const [interviews,      setInterviews]      = useState([]);
    const [loading,         setLoading]         = useState(true);
    const [feedbackFor,     setFeedbackFor]     = useState(null);
    const [viewFeedbackFor, setViewFeedbackFor] = useState(null);
    const [rescheduleFor,   setRescheduleFor]   = useState(null); // FEATURE: reschedule state
    const load = () => { setLoading(true); axiosInstance.get('/ats/interviews').then(r => setInterviews(r.data.data || [])).catch(() => {}).finally(() => setLoading(false)); };
    useEffect(load, []);
    return (
        <>
            <div className="d-flex justify-content-between mb-3 align-items-center">
                <span className="text-muted small">{interviews.length} interview(s)</span>
                <Button size="sm" variant="outline-secondary" onClick={load}><RefreshCw size={13} /></Button>
            </div>
            {loading ? <div className="text-center py-4"><Spinner animation="border" /></div> : (
                <Card className="border-0 shadow-sm">
                    <Table hover responsive className="mb-0 small align-middle">
                        <thead className="bg-light">
                            <tr className="text-muted text-uppercase" style={{ fontSize: 10 }}>
                                <th className="ps-4">Candidate</th><th>Job</th><th>Interviewer</th>
                                <th>Scheduled</th><th>Mode / Link</th><th>Status</th><th className="pe-4">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {interviews.length === 0 && <tr><td colSpan={7} className="text-center text-muted py-4">No interviews scheduled.</td></tr>}
                            {interviews.map(iv => (
                                <tr key={iv.id}>
                                    <td className="ps-4 fw-medium">{iv.candidate_name}</td>
                                    <td>{iv.job_title}</td>
                                    <td>{iv.interviewer_name}</td>
                                    <td>{fmtDate(iv.scheduled_at)}</td>
                                    <td>
                                        <div>{iv.mode}</div>
                                        {iv.meeting_link && (
                                            <a href={iv.meeting_link} target="_blank" rel="noopener noreferrer"
                                                className="d-flex align-items-center gap-1 text-primary" style={{ fontSize: 11 }}>
                                                <Video size={11} />Join Meeting
                                            </a>
                                        )}
                                    </td>
                                    <td>
                                        <Badge bg={iv.status === 'Completed' ? 'success' : iv.status === 'Cancelled' ? 'danger' : 'primary'} style={{ fontSize: 10 }}>
                                            {iv.status}
                                        </Badge>
                                    </td>
                                    <td className="pe-4">
                                        <div className="d-flex gap-1 flex-wrap">
                                            {/* Scheduled: Feedback + Reschedule */}
                                            {iv.status === 'Scheduled' && (
                                                <>
                                                    <Button size="sm" variant="outline-success"
                                                        style={{ fontSize: 11, borderRadius: 8, whiteSpace: 'nowrap' }}
                                                        onClick={() => setFeedbackFor(iv)}>
                                                        <MessageSquare size={12} className="me-1" />Feedback
                                                    </Button>
                                                    {/* FEATURE: Reschedule button */}
                                                    <Button size="sm" variant="outline-warning"
                                                        style={{ fontSize: 11, borderRadius: 8, whiteSpace: 'nowrap' }}
                                                        onClick={() => setRescheduleFor(iv)}>
                                                        <RefreshCw size={11} className="me-1" />Reschedule
                                                    </Button>
                                                </>
                                            )}
                                            {/* Completed: View Feedback or Done */}
                                            {iv.status === 'Completed' && (
                                                iv.feedback_rating ? (
                                                    <Button size="sm" variant="outline-secondary"
                                                        style={{ fontSize: 11, borderRadius: 8, whiteSpace: 'nowrap' }}
                                                        onClick={() => setViewFeedbackFor(iv)}>
                                                        <Star size={12} className="me-1" />View Feedback
                                                    </Button>
                                                ) : (
                                                    <span style={{ fontSize: 11, color: '#059669' }}>
                                                        <CheckCircle size={12} className="me-1" />Done
                                                    </span>
                                                )
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Card>
            )}
            {feedbackFor     && <FeedbackModal interview={feedbackFor} onClose={() => setFeedbackFor(null)} onDone={load} />}
            {viewFeedbackFor && <ViewFeedbackModal interview={viewFeedbackFor} onClose={() => setViewFeedbackFor(null)} />}
            {/* FEATURE: Reschedule modal */}
            <RescheduleInterviewModal
                show={!!rescheduleFor}
                interview={rescheduleFor}
                onClose={() => setRescheduleFor(null)}
                onDone={() => { setRescheduleFor(null); load(); }}
            />
        </>
    );
};
export default ATSPage;
